<template>
	<div id="app" class="school-map">
		<!-- Create filters options -->
		<div class="filters-wrapper">
			<input-select
				:filter="filter"
				v-for="filter in filters"
				:key="filter.machineName"
				@filtered="setFilterValue"
				:ref="filter.machineName"
			/>
		</div>
		<div class="school-map-container">
			<div class="schools-wrapper" ref="schoolscontainer">
				<div class="results-number">
					{{ getSchoolNb }} {{ words.results }}
				</div>
				<div
					class="schools-container"
					:class="currentMidx === school.id ? 'selected' : null"
					v-for="school in getSchoolList"
					:key="school.id"
					@click="toggleInfoWindow(school.id)"
					:ref="currentMidx === school.id ? 'selected' : null"
				>
					<p class="school-name">{{ school.name }}</p>
					<p>{{ school.address }}</p>
					<p>{{ school.postal }} {{ school.city }}</p>
					<p>{{ school.phone }}</p>
					<p class="school-level-title">
						{{ words.level }}
						<span
							class="school-level-span"
							v-for="(level, index) in school.level"
							:key="index"
						>
							{{ withCommas(index, school.level) }}
						</span>
					</p>

					<p class="school-language-title">
						{{ words.language }}
						<span
							class="school-language-span"
							v-for="(language, index) in school.language"
							:key="index"
						>
							{{ withCommas(index, school.language) }}
						</span>
					</p>
				</div>
			</div>

			<div class="map-wrapper">
				<GmapMap :center="center" :zoom="6" ref="gmap">
					<GmapInfoWindow
						:options="infoOptions"
						:position="infoWindowPos"
						:opened="infoWinOpen"
						@closeclick="infoWinOpen = false"
					>
					</GmapInfoWindow>

					<GmapCluster :zoom-on-click="true">
						<GmapMarker
							:key="school.id"
							v-for="school in getSchoolList"
							:position="school.position"
							:clickable="true"
							:draggable="false"
							@click="toggleInfoWindow(school.id)"
						/>
					</GmapCluster>
				</GmapMap>
			</div>
		</div>
	</div>
</template>

<script>
import InputSelect from "./InputSelect";
import Vue from "vue";
import { filter, some, includes, isEmpty } from "lodash";
export default {
	name: "app",
	components: { InputSelect },
	props: {
		schools: {
			type: Array,
			default() {
				return [];
			}
		},
		filters: {
			type: Array,
			default() {
				return [];
			}
		},
		words: {
			type: Object,
			default() {
				return [];
			}
		}
	},
	data() {
		return {
			//Google Map
			center: { lat: 46.227638, lng: 2.213749 },
			//Select for Filters
			filterValues: {
				department: null,
				area: null,
				language: "",
				level: ""
			},
			infoWindowPos: null,
			infoWinOpen: false,
			currentMidx: null,

			infoOptions: {
				content: "",
				//optional: offset infowindow so it visually sits nicely on top of our marker
				pixelOffset: {
					width: 0,
					height: -35
				}
			}
		};
	},
	watch: {
		"filterValues.department"(newValue) {
			if (newValue === null) {
				return;
			}

			this.$refs.area[0].resetValues();
		},
		"filterValues.area"(newValue) {
			if (newValue === null) {
				return;
			}

			this.$refs.department[0].resetValues();
		}
	},
	computed: {
		filteredSchools() {
			let listSchools = this.schools;

			if (
				this.filterValues.area !== null &&
				this.filterValues.area !== ""
			) {
				listSchools = filter(listSchools, {
					region: this.filterValues.area
				});
			}
			if (this.filterValues.level !== "") {
				let selectLevel = this.filterValues.level;
				listSchools = filter(listSchools, function(school) {
					return some(school.level, function(level) {
						return includes(selectLevel, level);
					});
				});
			}
			if (
				this.filterValues.department !== null &&
				this.filterValues.department !== ""
			) {
				listSchools = filter(listSchools, {
					department: this.filterValues.department
				});
			}
			if (this.filterValues.language !== "") {
				let selectLanguage = this.filterValues.language;
				listSchools = filter(listSchools, function(school) {
					return some(school.language, function(language) {
						return includes(selectLanguage, language);
					});
				});
			}
			return listSchools;
		},
		getSchoolList() {
			//if 0 filters/fields are selected show schools
			//else show filtered schools
			if (
				(this.filterValues.department == null ||
					isEmpty(this.filterValues.department)) &&
				(this.filterValues.area == null ||
					isEmpty(this.filterValues.area)) &&
				isEmpty(this.filterValues.language) &&
				isEmpty(this.filterValues.level)
			) {
				return this.schools;
			}

			return this.filteredSchools;
		},
		getSchoolNb() {
			if (
				(this.filterValues.department == null ||
					isEmpty(this.filterValues.department)) &&
				(this.filterValues.area == null ||
					isEmpty(this.filterValues.area)) &&
				isEmpty(this.filterValues.language) &&
				isEmpty(this.filterValues.level)
			) {
				return this.schools.length;
			}

			return this.filteredSchools.length;
		}
	},
	methods: {
		scrollParentToChild(parent, child) {
			// Where is the parent on page
			const parentRect = parent.getBoundingClientRect();
			// What can you see?
			const parentViewableArea = {
				height: parent.clientHeight,
				width: parent.clientWidth
			};

			// Where is the child
			const childRect = child.getBoundingClientRect();
			// Is the child viewable?
			const isViewable =
				childRect.top >= parentRect.top &&
				childRect.top <= parentRect.top + parentViewableArea.height;

			// if you can't see the child try to scroll parent
			if (!isViewable) {
				// scroll by offset relative to parent
				parent.scrollTop =
					childRect.top + parent.scrollTop - parentRect.top;
			}
		},
		withCommas(index, schoolTags) {
			if (index !== schoolTags.length - 1) {
				return `${schoolTags[index]},`;
			} else {
				return schoolTags[index];
			}
		},
		setFilterValue({ key, value }) {
			Vue.set(this.filterValues, key, value);
		},
		zoomToMarker(position) {
			this.$refs.gmap.$mapObject.setZoom(17);
			this.$refs.gmap.panTo(position);
		},
		scrollSchoolIntoView() {
			setTimeout(() => {
				const selected = this.$refs.selected[0];
				const parent = this.$refs.schoolscontainer;

				this.scrollParentToChild(parent, selected);
			}, 0);
		},
		toggleInfoWindow(id) {
			const filteredSchools = this.schools.filter(
				school => school.id === id
			);
			if (filteredSchools.length === 0) {
				return;
			}

			const marker = filteredSchools[0];

			this.zoomToMarker(marker.position);
			this.scrollSchoolIntoView(marker.id);
			this.infoWindowPos = marker.position;
			this.infoOptions.content =
				"<p><strong>" +
				marker.name +
				"</strong></p> <p>" +
				marker.address +
				"</p>  <p>" +
				marker.postal +
				" - " +
				marker.city +
				"</p>";

			//check if its the same marker that was selected if yes toggle
			if (this.currentMidx == id) {
				this.infoWinOpen = !this.infoWinOpen;
			}
			//if different marker set infowindow to open and reset current marker index
			else {
				this.infoWinOpen = true;
				this.currentMidx = id;
			}
		}
	}
};
</script>
